import { useState } from 'react';
import {
  importCompaniesFromExcel,
  importSalesRepsFromExcel,
  importPartiesFromExcel,
  importItemCodesFromExcel,
  importPriceListFromExcel,
} from '../../lib/excelImport';
import { downloadExcelTemplate, type ImportTemplateType } from '../../lib/excelTemplates';
import { logUserActivity } from '../../lib/activityLogger';

type ImportType = 'companies' | 'sales_reps' | 'parties' | 'items' | 'prices';

export default function ImportPage() {
  const [importType, setImportType] = useState<ImportType>('parties');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; errors?: string[] } | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  async function processFile(file: File) {
    setImporting(true);
    setResult(null);

    try {
      let importResult;
      switch (importType) {
        case 'companies':
          importResult = await importCompaniesFromExcel(file);
          break;
        case 'sales_reps':
          importResult = await importSalesRepsFromExcel(file);
          break;
        case 'parties':
          importResult = await importPartiesFromExcel(file);
          break;
        case 'items':
          importResult = await importItemCodesFromExcel(file);
          break;
        case 'prices':
          importResult = await importPriceListFromExcel(file);
          break;
      }
      const resPayload = importResult || { success: false, message: 'Unknown import type' };
      setResult(resPayload);
      logUserActivity({
        action_type: 'EXCEL_UPLOADED',
        resource_id: file.name,
        details: `Imported ${(file.size / 1024).toFixed(1)} KB into [${importType}]: ${resPayload.message}`,
      });
    } catch (err) {
      setResult({ success: false, message: 'Failed to import file. Please check file format.' });
      logUserActivity({
        action_type: 'EXCEL_UPLOADED',
        resource_id: file.name,
        details: `Failed upload attempt into [${importType}]`,
      });
      console.error(err);
    } finally {
      setImporting(false);
    }
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
    e.target.value = '';
  }

  const importOptions = [
    { value: 'parties', label: 'Customer Parties', icon: '👥', description: 'Bulk import corporate clients, contact persons, phones, and GST numbers' },
    { value: 'prices', label: 'Price List Matrices', icon: '🏷️', description: 'Update component rates for castors, wheels, bearings, and bushes' },
    { value: 'items', label: 'Products / Finished Items', icon: '⚙️', description: 'Add finished castor wheel assembly codes and technical specifications' },
    { value: 'companies', label: 'Operating Companies', icon: '🏢', description: 'Update manufacturing plants, bank details, addresses, and signatory' },
    { value: 'sales_reps', label: 'Sales Representatives', icon: '👔', description: 'Register sales executives and assign quotation initials' },
  ];

  const selectedOption = importOptions.find((o) => o.value === importType);

  function handleDownloadTemplate() {
    downloadExcelTemplate(importType as ImportTemplateType);
    logUserActivity({
      action_type: 'EXCEL_GENERATED',
      resource_id: `${selectedOption?.label || importType} Template (.xlsx)`,
      details: `Downloaded import template for ${selectedOption?.label || importType}`,
    });
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header Bar */}
      <div>
        <h1 className="text-2xl font-black tracking-tight text-slate-900">Bulk Excel Data Import</h1>
        <p className="mt-0.5 text-xs text-slate-500">
          Upload Microsoft Excel spreadsheets (.xlsx, .xls) to batch update master databases and pricing matrices
        </p>
      </div>

      {/* Step 1: Select Category */}
      <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-6 space-y-4">
        <div className="flex justify-between items-center border-b border-slate-100 pb-3">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Step 1</span>
            <h2 className="text-base font-bold text-slate-900">Select Data Category & Download Template</h2>
          </div>
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white text-xs font-bold px-3.5 py-2 rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <span>📥</span>
            <span>Download {selectedOption?.label} Template (.xlsx)</span>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {importOptions.map((option) => {
            const isSelected = importType === option.value;
            return (
              <div
                key={option.value}
                onClick={() => {
                  setImportType(option.value as ImportType);
                  setResult(null);
                }}
                className={`p-4 rounded-xl border-2 transition-all cursor-pointer flex items-start gap-3.5 ${
                  isSelected
                    ? 'border-blue-600 bg-blue-50/50 shadow-sm'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div
                  className={`h-9 w-9 rounded-lg flex items-center justify-center text-lg flex-shrink-0 ${
                    isSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {option.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-xs text-slate-900">{option.label}</span>
                    <input
                      type="radio"
                      name="importType"
                      checked={isSelected}
                      onChange={() => setImportType(option.value as ImportType)}
                      className="h-4 w-4 text-blue-600 border-slate-300"
                    />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{option.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Step 2: Upload Excel File */}
      <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-6 space-y-4">
        <div className="border-b border-slate-100 pb-3">
          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Step 2</span>
          <h2 className="text-base font-bold text-slate-900">
            Upload Populated Excel Spreadsheet for {selectedOption?.label}
          </h2>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            const file = e.dataTransfer.files?.[0];
            if (file) processFile(file);
          }}
          className={`border-2 border-dashed rounded-2xl p-8 sm:p-12 text-center transition-all ${
            isDragOver
              ? 'border-blue-600 bg-blue-50/60 scale-[1.01]'
              : 'border-slate-300 hover:border-slate-400 bg-slate-50/50'
          }`}
        >
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            disabled={importing}
            className="hidden"
            id="excel-upload"
          />
          <label htmlFor="excel-upload" className="cursor-pointer flex flex-col items-center">
            <div className="h-14 w-14 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center text-3xl mb-3 shadow-inner">
              📄
            </div>
            <span className="text-sm font-bold text-slate-900">
              {importing ? 'Processing & Validating Spreadsheet...' : 'Choose or Drag & Drop Excel File'}
            </span>
            <span className="text-xs text-slate-500 mt-1">
              Supports Microsoft Excel (.xlsx, .xls) up to 20 MB
            </span>
            <div className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-sm transition-colors">
              Browse Local File
            </div>
          </label>
        </div>

        {/* Status / Results Banner */}
        {result && (
          <div
            className={`p-4 rounded-xl text-xs font-medium border ${
              result.success
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <div className="flex items-center gap-2 font-bold text-sm">
              <span>{result.success ? '✓' : '✕'}</span>
              <span>{result.message}</span>
            </div>
            {result.errors && result.errors.length > 0 && (
              <ul className="mt-2.5 list-disc list-inside space-y-1 text-[11px] text-red-700 font-mono">
                {result.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Field Format Expectations */}
      <div className="bg-slate-100 rounded-2xl p-6 border border-slate-200 text-xs text-slate-700 space-y-2">
        <h3 className="font-bold text-slate-900 flex items-center gap-1.5">
          <span>📋</span> Excel Column Specifications
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1 text-[11px] leading-relaxed">
          <p><strong>Customer Parties:</strong> Name, Contact Person, Phone, Email, Address 1, Address 2, Address 3, GST</p>
          <p><strong>Price List:</strong> Category, Item Code, Item Name, Size, Price, Effective Date</p>
          <p><strong>Products:</strong> Code, Description, Castor Type, Wheel Material, Size, Base Price</p>
          <p><strong>Companies:</strong> Code, Legal Name, GST, Address, Bank Name, Account Number, IFSC</p>
        </div>
      </div>
    </div>
  );
}
